import {createHooks} from "@wordpress/hooks";

/**
 * Actions - Shared event bus for controllers, gateways, and cart
 *
 * Provides a centralized event system that allows different components
 * to communicate through actions and filters. Implemented as a singleton
 * to ensure all components share the same event bus instance.
 *
 * @example
 * import Actions from './core/Actions';
 *
 * const actions = new Actions(); // Gets singleton instance
 *
 * // Listen to events
 * actions.addAction('before_payment_sheet_open', (gateway) => {
 *   console.log('Payment sheet opening');
 * });
 *
 * // Trigger events
 * actions.doAction('before_payment_sheet_open', this);
 *
 * // Apply filters
 * const data = actions.applyFilters('cartData', {total: 100}, this);
 */
class Actions {
    constructor() {
        // Singleton pattern - return existing instance if already created
        if (Actions.instance) {
            return Actions.instance;
        }

        this.hooks = createHooks();
        Actions.instance = this;
    }

    /**
     * Add an action callback
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     * @param {number} priority - Priority (default 10)
     */
    addAction(event, callback, priority = 10) {
        this.hooks.addAction(event, 'wcStripe', callback, priority);
    }

    /**
     * Trigger an action
     * @param {string} event - Event name
     * @param {...*} args - Arguments to pass to callbacks
     */
    doAction(event, ...args) {
        this.hooks.doAction(event, ...args);
    }

    /**
     * Add a filter callback
     * @param {string} event - Filter name
     * @param {Function} callback - Callback function
     * @param {number} priority - Priority (default 10)
     */
    addFilter(event, callback, priority = 10) {
        this.hooks.addFilter(event, 'wcStripe', callback, priority);
    }

    /**
     * Apply filters
     * @param {string} event - Filter name
     * @param {...*} args - Arguments to pass to callbacks (first arg is the value to filter)
     * @returns {*} Filtered value
     */
    applyFilters(event, ...args) {
        return this.hooks.applyFilters(event, ...args);
    }
}

export default new Actions();